import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { MInputField } from "@/components/ui/atoms/MInputField";
import MDateInputField from "@/components/ui/molecules/MDateInputField";
import { MButton } from "@/components/ui/atoms/MButtons";
import { useTypeAhead } from "@/hooks/useTypeAhead";
import { useProfileField } from "@/hooks/useHealthProfile";
import { useToast } from "@/components/ui/atoms/MToastProvider";
import MDropDownDrawer from "@/components/ui/molecules/MDropDownDrawer";
import { ICondition } from "@/types/health-profile";
import { getMemberInfo } from "@/hooks/useMembers";
import FormHeader from "@/components/ui/organisms/MHeader";
import { IconCaretDownFilled } from "@tabler/icons-react";
import { MedicalDetailType } from "@/types/health-profile";
import MSpacer from "@/components/ui/atoms/MSpacer";
import { useTranslation } from "react-i18next";
import { trackUserDataLoaded } from "@/utils/gtm";
import {
  ANALYTICS_SCREEN_TYPES,
  ANALYTICS_SCREEN_NAMES,
} from "@/constants/analytics.constants";
import MLoader from "@/components/ui/atoms/MLoader";
import { sortByYearMonth } from "@/utils/date-utils";

const ConditionsForm: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const mode = params.get("mode") as "add" | "edit";
  const editIndex = params.get("index")
    ? Number(params.get("index"))
    : undefined;
  // Use getMemberInfo() to get memberId
  const memberInfo = getMemberInfo();
  const memberId = memberInfo.id;

  const { showToast } = useToast();
  const [customConditionName, setCustomConditionName] = useState("");
  const [customDiagnosisYear, setCustomDiagnosisYear] = useState<number | null>(
    null
  );
  const [customDiagnosisMonth, setCustomDiagnosisMonth] = useState<
    number | null
  >(null);
  const [showConditionDrawer, setShowConditionDrawer] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isOther, setIsOther] = useState(false);
  const [otherValue, setOtherValue] = useState("");

  const category = `long_term_conditions_${memberInfo.memberType}`;
  const { values: typeAheadOptions } = useTypeAhead(category);
  const { fieldValue: conditions = [], saveField: saveConditions } =
    useProfileField<ICondition[]>(memberId, MedicalDetailType.CONDITIONS, []);

  // Track page load for add mode only
  useEffect(() => {
    if (mode === "add") {
      trackUserDataLoaded({
        memberInfo,
        screenType: ANALYTICS_SCREEN_TYPES.PROFILE,
        screenName: ANALYTICS_SCREEN_NAMES.LONG_TERM_CONDITION_ADD,
      });
    }
  }, [mode]);

  useEffect(() => {
    if (mode === "edit" && editIndex !== undefined && conditions[editIndex]) {
      const condition = conditions[editIndex];
      setCustomConditionName(condition.name);
      setIsOther(condition.name === "Other");
      setOtherValue("");
      setCustomDiagnosisYear(condition.diagnosisYear || null);
      setCustomDiagnosisMonth(condition.diagnosisMonth || null);
    }
    if (mode === "add") {
      setCustomConditionName("");
      setIsOther(false);
      setOtherValue("");
      setCustomDiagnosisYear(null);
      setCustomDiagnosisMonth(null);
    }
  }, [mode, editIndex, conditions]);

  const getConditionOptions = () => {
    const options = typeAheadOptions.map((option) => ({
      value: option,
      label: option,
    }));
    options.push({
      value: t("conditions.other"),
      label: t("conditions.other"),
    });
    return options;
  };

  const navigateToConditionsPage = () => {
    navigate("/profile/health-profile/conditions");
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Track completion
      trackUserDataLoaded({
        memberInfo,
        screenType: ANALYTICS_SCREEN_TYPES.PROFILE,
        screenName: ANALYTICS_SCREEN_NAMES.LONG_TERM_CONDITION_ADD_COMPLETE,
      });

      let updated: ICondition[];
      const finalConditionName = isOther ? otherValue : customConditionName;
      if (mode === "edit" && editIndex !== undefined) {
        updated = [...conditions];
        updated[editIndex] = {
          ...updated[editIndex],
          name: finalConditionName,
          diagnosisYear: customDiagnosisYear,
          diagnosisMonth: customDiagnosisMonth,
        };
      } else {
        updated = [
          ...conditions,
          {
            name: finalConditionName,
            diagnosisYear: customDiagnosisYear,
            diagnosisMonth: customDiagnosisMonth,
          },
        ];
      }
      await saveConditions(sortByYearMonth(updated));

      // Navigate to conditions page
      navigateToConditionsPage();
    } catch (e) {
      showToast({
        message: t("errors.toast"),
        variant: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleConditionSelect = (val: string) => {
    if (val === "Other") {
      setShowConditionDrawer(false);
      setOtherValue("");
      setIsOther(true);
      setCustomConditionName("Other");
    } else {
      setIsOther(false);
      setCustomConditionName(val);
      setOtherValue("");
      setShowConditionDrawer(false);
    }
  };

  const checkIsValid = () => {
    return (
      (isOther
        ? otherValue.trim().length === 0
        : customConditionName.trim().length === 0) ||
      customDiagnosisYear === null ||
      customDiagnosisMonth === null ||
      saving
    );
  };

  if (saving) return <MLoader />;

  return (
    <div
      className="profile-page-root flex flex-col"
      style={{ minHeight: "calc(var(--vh, 1vh) * 100 - 128px)" }}
    >
      <FormHeader module="add_conditions" memberInfo={memberInfo} />
      <MSpacer />
      <div className="profile-input-container flex-1">
        <MInputField
          type="text"
          label={t("conditions.what_is_condition")}
          value={customConditionName}
          placeholder={t("common.notAnswered")}
          size="md"
          showIcon
          icon={<IconCaretDownFilled />}
          onClick={() => setShowConditionDrawer(true)}
        />
        {isOther && (
          <MInputField
            type="text"
            label={t("conditions.name")}
            value={otherValue}
            onChange={(e) => setOtherValue(e.target.value)}
            placeholder={t("common.notAnswered")}
            size="md"
          />
        )}
        <MDateInputField
          hideDate
          label={t("conditions.since_when")}
          minYear={memberInfo.dobYear || 1940}
          value={
            customDiagnosisYear && customDiagnosisMonth
              ? new Date(customDiagnosisYear, customDiagnosisMonth - 1)
              : undefined
          }
          onDateChange={(date) => {
            setCustomDiagnosisYear(date.getFullYear());
            setCustomDiagnosisMonth(date.getMonth() + 1);
          }}
          formatDate={(date) =>
            date
              ? `${date.toLocaleString("default", {
                  month: "long",
                })} ${date.getFullYear()}`
              : ""
          }
          placeholder="Not Answered"
          size="md"
          datePickerTitle="Select date"
        />
      </div>
      <MDropDownDrawer
        open={showConditionDrawer}
        onClose={() => setShowConditionDrawer(false)}
        title={t("conditions.select")}
        items={getConditionOptions()}
        initialValue={customConditionName}
        onSelect={handleConditionSelect}
        variant="single"
        height="70vh"
      />

      <div className="confirm-footer">
        <MButton
          type="button"
          variant="filled"
          size="lg"
          className="w-full h-[56px]"
          disabled={checkIsValid()}
          onClick={handleSave}
        >
          {saving ? "Saving..." : "Done"}
        </MButton>
      </div>
    </div>
  );
};

export default ConditionsForm;
